import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowLeft, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ loading: true });
    const [polls, setPolls] = useState(0);

    useEffect(() => {
        if (!sessionId) {
            setStatus({ loading: false, error: "Missing session." });
            return;
        }
        let active = true;
        let timer;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/shop/checkout/status/${sessionId}`);
                if (!active) return;
                if (data.payment_status === "paid" || data.status === "complete") {
                    setStatus({ loading: false, ...data });
                    return;
                }
                if (polls >= 30) {
                    setStatus({
                        loading: false,
                        delayed: true,
                        email: data.email,
                        product_name: data.product_name,
                        product_type: data.product_type,
                    });
                    return;
                }
                setPolls((p) => p + 1);
                timer = setTimeout(poll, 2000);
            } catch {
                if (active) setStatus({ loading: false, error: "Could not confirm payment." });
            }
        };
        poll();
        return () => { active = false; clearTimeout(timer); };
        // eslint-disable-next-line
    }, [sessionId]);

    return (
        <main className="bg-[#fbf9f8] min-h-screen pt-36 pb-24" data-testid="shop-success">
            <div className="max-w-[700px] mx-auto px-6 md:px-10 text-center">
                {status.loading ? (
                    <p className="font-serif italic text-2xl text-[#0c0f3d]">Confirming your order…</p>
                ) : status.error ? (
                    <>
                        <p className="font-serif text-3xl md:text-4xl text-[#0c0f3d] leading-tight">{status.error}</p>
                        <Link to="/shop" className="inline-flex items-center gap-3 mt-10 px-8 py-4 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] transition-colors">
                            <ArrowLeft size={14} /> Back to Shop
                        </Link>
                    </>
                ) : status.delayed ? (
                    <>
                        <CheckCircle2 size={48} className="text-[#e47a6a] mx-auto mb-6" />
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#e47a6a] mb-4">Payment Processing</p>
                        <h1 className="font-serif text-3xl md:text-5xl text-[#0c0f3d] leading-[1.1] tracking-tight">
                            Your order is being <span className="italic text-[#e47a6a]">finalized.</span>
                        </h1>
                        <p className="font-sans text-base text-[#0c0f3d]/75 mt-8 leading-[1.8]">
                            Your payment has been submitted. We&apos;ll email <span className="font-medium">{status.email}</span> with the confirmation within a few minutes. You can safely close this page.
                        </p>
                        <Link to="/shop" className="inline-flex items-center gap-3 mt-10 px-8 py-4 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-[#fbf9f8] transition-colors">
                            <ArrowLeft size={14} /> Continue Shopping
                        </Link>
                    </>
                ) : (
                    <>
                        <CheckCircle2 size={48} className="text-[#e47a6a] mx-auto mb-6" />
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#e47a6a] mb-4">Thank You</p>
                        <h1 className="font-serif text-4xl md:text-6xl text-[#0c0f3d] leading-[1] tracking-tight">
                            Your order is <span className="italic text-[#e47a6a]">received.</span>
                        </h1>
                        {status.product_name && (
                            <p className="font-serif italic text-xl md:text-2xl text-[#0c0f3d]/75 mt-8">
                                {status.product_name}
                            </p>
                        )}
                        <div className="mt-12 bg-[#e3c7b9]/40 p-8 text-left">
                            {status.product_type === "digital" ? (
                                <>
                                    <Download size={22} className="text-[#e47a6a] mb-3" />
                                    <p className="font-serif text-xl text-[#0c0f3d] leading-tight">Digital delivery</p>
                                    <p className="font-sans text-sm text-[#0c0f3d]/75 mt-3 leading-[1.8]">
                                        We&apos;ll email your download link to <span className="font-medium">{status.email}</span> within 24 hours. Check your inbox (and spam folder, just in case).
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="font-serif text-xl text-[#0c0f3d] leading-tight">What happens next</p>
                                    <ol className="mt-4 space-y-2 font-sans text-sm text-[#0c0f3d]/80 list-decimal pl-5 leading-[1.8]">
                                        <li>You&apos;ll receive an order confirmation at <span className="font-medium">{status.email}</span></li>
                                        <li>Your item ships via USPS within 3&#8209;5 business days</li>
                                        <li>Tracking information will follow once your order is on the way</li>
                                    </ol>
                                </>
                            )}
                        </div>
                        <Link to="/shop" className="inline-flex items-center gap-3 mt-12 px-8 py-4 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#0c0f3d] hover:text-[#fbf9f8] transition-colors">
                            <ArrowLeft size={14} /> Continue Shopping
                        </Link>
                    </>
                )}
            </div>
        </main>
    );
}
